import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../../shared/state/pending-action-tracker';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../../shared/page-layout/page-data-panel.component';
import { PageActionPaneComponent } from '../../../../shared/page-layout/page-action-pane.component';
import { FinancePdfViewButtonComponent } from '../../../finance-pdf-view-button.component';
import { AccountsService, FinancePdfStatus } from '../../../accounts.service';
import { Voucher } from '../voucher.model';
import { AccountingDocumentAction, DocumentAuditEvent, GlPreview } from '../../../accounting-lifecycle.model';
import { AccountingTabLabelComponent } from '../../../shared/accounting-tab-label.component';
import { AccountingAuditTimelineComponent } from '../../../shared/accounting-audit-timeline.component';
import { AccountingGlPreviewPanelComponent } from '../../../shared/accounting-gl-preview-panel.component';
import { AccountingAttachmentsPanelComponent } from '../../../shared/accounting-attachments-panel.component';
import { ManualSourceBadgeComponent } from '../../../shared/manual-source-badge.component';
import { CrmPartyContextService } from '../../../../shared/crm-client-contact-picker/crm-party-context.service';
import { AccountingWorkflowActionsComponent } from '../../../shared/accounting-workflow-actions.component';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { PermissionService } from '../../../../security/permission.service';
import { app_permissions } from '../../../../security/app-permissions';

@Component({
    selector: 'app-voucher-detail',
    standalone: true,
    imports: [FinancePdfViewButtonComponent, CommonModule, ButtonModule, ToastModule, TableModule, TabsModule, TagModule, TooltipModule, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent, PageActionPaneComponent, AccountingTabLabelComponent, AccountingAuditTimelineComponent, AccountingGlPreviewPanelComponent, AccountingAttachmentsPanelComponent, AccountingWorkflowActionsComponent, ManualSourceBadgeComponent],
    template: `
        <app-page-shell>
            <p-toast position="top-center"></p-toast>
            <app-page-detail-hero [auditStamp]="voucher" eyebrow="Vendor Voucher" [title]="voucher?.voucherNumber || 'Voucher'"
                [subtitle]="voucher ? crmPartyContext.displayName(voucher) : 'Voucher allocation detail'" icon="pi pi-wallet">
                <div tags>
                    <p-tag [value]="voucher?.allocationStatus || 'UNAPPLIED'" severity="info"></p-tag>
                    <p-tag [value]="voucher?.documentStatus || 'UNKNOWN'" severity="secondary"></p-tag>
                    <p-tag [value]="voucher?.postingStatus || 'NOT_POSTED'" severity="warn"></p-tag>
                </div>
            </app-page-detail-hero>
            <div class="document-detail-layout" *ngIf="voucher">
                <div class="document-detail-content">
            <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
                <p-tablist>
                    <p-tab value="overview"><app-accounting-tab-label icon="pi pi-info-circle" label="Overview"></app-accounting-tab-label></p-tab>
                    <p-tab value="allocations"><app-accounting-tab-label icon="pi pi-link" label="Allocations"></app-accounting-tab-label></p-tab>
                    <p-tab value="bills"><app-accounting-tab-label icon="pi pi-file" label="Bills"></app-accounting-tab-label></p-tab>
                    <p-tab value="journal"><app-accounting-tab-label icon="pi pi-book" label="Journal"></app-accounting-tab-label></p-tab>
                    <p-tab value="attachments"><app-accounting-tab-label icon="pi pi-paperclip" label="Attachments"></app-accounting-tab-label></p-tab>
                    <p-tab value="activity"><app-accounting-tab-label icon="pi pi-history" label="Activity"></app-accounting-tab-label></p-tab>
                    <p-tab value="related"><app-accounting-tab-label icon="pi pi-share-alt" label="Related"></app-accounting-tab-label></p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="overview"><app-page-data-panel title="Voucher Summary"><div class="detail-grid"><div><strong>Amount</strong><span>{{ money(voucher.amount, voucher.country?.currencyCode) }}</span></div><div><strong>Allocated</strong><span>{{ money(voucher.allocatedAmount, voucher.country?.currencyCode) }}</span></div><div><strong>Open</strong><span>{{ money(voucher.openAmount, voucher.country?.currencyCode) }}</span></div><div><strong>Payment transaction</strong><span>{{ voucher.paymentTransactionNumber || '-' }}</span></div></div></app-page-data-panel></p-tabpanel>
                    <p-tabpanel value="allocations"><app-page-data-panel title="Allocation Summary"><div class="detail-grid"><div><strong>Status</strong><span>{{ voucher.allocationStatus || 'UNAPPLIED' }}</span></div><div><strong>Allocated</strong><span>{{ money(voucher.allocatedAmount, voucher.country?.currencyCode) }}</span></div><div><strong>Open</strong><span>{{ money(voucher.openAmount, voucher.country?.currencyCode) }}</span></div></div></app-page-data-panel></p-tabpanel>
                    <p-tabpanel value="bills"><app-page-data-panel title="Linked bills"><p-table [value]="voucher.bills || []"><ng-template pTemplate="header"><tr><th>Bill</th><th>Amount</th><th>Status</th></tr></ng-template><ng-template pTemplate="body" let-bill><tr><td>{{ bill.billNumber }}</td><td>{{ money(bill.allocatedAmount || bill.totalAmount, voucher.country?.currencyCode) }}</td><td>{{ bill.allocationStatus }}</td></tr></ng-template></p-table></app-page-data-panel></p-tabpanel>
                    <p-tabpanel value="journal"><app-accounting-gl-preview-panel [preview]="glPreview"></app-accounting-gl-preview-panel></p-tabpanel>
                    <p-tabpanel value="attachments"><app-accounting-attachments-panel documentType="VOUCHER" [documentId]="voucher.voucherId" [attachments]="$any(voucher).attachments || []"></app-accounting-attachments-panel></p-tabpanel>
                    <p-tabpanel value="activity"><app-accounting-audit-timeline [events]="auditEvents"></app-accounting-audit-timeline></p-tabpanel>
                    <p-tabpanel value="related"><app-page-data-panel title="Related"><div class="detail-grid"><div><strong>Source</strong><span><app-manual-source-badge [sourceType]="voucher.sourceType || 'SYSTEM'" [sourceReference]="voucher.sourceReference" [manualReasonCode]="voucher.manualReasonCode"></app-manual-source-badge></span></div><div><strong>From bank</strong><span>{{ voucher.fromBankAccount?.accountName || '-' }}</span></div><div><strong>To bank</strong><span>{{ voucher.toBankAccount?.accountName || '-' }}</span></div></div></app-page-data-panel></p-tabpanel>
                </p-tabpanels>
            </p-tabs>
                </div>
                <app-page-action-pane title="Voucher Actions" subtitle="Access payment evidence and return to vendor vouchers.">
                    <div class="action-buttons">
                        <p-button *ngIf="canEditDraft" label="Edit Draft" icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="editDraft()"></p-button>
                        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="back()"></p-button>
                        <app-finance-pdf-view-button kind="voucher" [documentId]="voucher.voucherId" [documentReference]="voucher.voucherNumber" [postingStatus]="voucher.postingStatus" [disabled]="!pdfStatus || pdfStatus.status === 'MISSING' || documentActions.isPending('download')"></app-finance-pdf-view-button>
                        <p-button [label]="pdfStatus?.status === 'ORIGINAL' ? 'Download original PDF' : pdfStatus?.status === 'LEGACY' ? 'Download legacy stored PDF' : pdfStatus?.status === 'MISSING' ? 'Original unavailable' : pdfStatus?.status === 'DRAFT' ? 'Preview draft PDF' : 'Checking PDF'"
                            [icon]="pdfStatus?.status === 'DRAFT' ? 'pi pi-print' : 'pi pi-download'" severity="info" [outlined]="true"
                            [pTooltip]="pdfStatus?.status === 'DRAFT' ? 'Generate a preview from the latest saved changes.' : 'Download the stored voucher PDF.'" [loading]="documentActions.isPending('download')" [disabled]="!pdfStatus"
                            (click)="download()"></p-button>
                    </div>
                    <div class="action-section action-section--primary" *ngIf="workflowActions.length">
                        <span class="action-section-title">Accounting</span>
                        <app-accounting-workflow-actions documentType="VOUCHER" [postedAt]="voucher.postedAt" [documentStatus]="voucher.documentStatus" [postingStatus]="voucher.postingStatus"
                            [allowedActions]="workflowActions" [voidMessage]="voidWarning" [loadingAction]="workflowLoadingAction"
                            (workflow)="workflow($event.action, $event.reason)" (post)="workflow('post')"></app-accounting-workflow-actions>
                    </div>
                </app-page-action-pane>
            </div>
        </app-page-shell>
    `,
    styles: [`.detail-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:.75rem}.detail-grid div{display:flex;flex-direction:column;gap:.25rem;padding:.75rem;border:1px solid #e2e8f0;border-radius:8px}.document-detail-layout{display:grid;grid-template-columns:minmax(0,1fr) minmax(20rem,22rem);gap:1rem;align-items:start}.document-detail-content{min-width:0}@media (max-width:1080px){.document-detail-layout{grid-template-columns:1fr}}`]
})
export class VoucherDetailComponent implements OnInit {
    pdfStatus?: FinancePdfStatus;
    readonly documentActions = new PendingActionTracker<string>();
    voucher?: Voucher;
    auditEvents: DocumentAuditEvent[] = [];
    glPreview?: GlPreview;
    activeTab = 'overview';
    canVoid = false;
    workflowLoadingAction: AccountingDocumentAction | null = null;
    readonly voidWarning = 'Voiding this voucher reverses all active vendor-bill applications and reopens the affected bills. This cannot be undone.';

    get workflowActions(): AccountingDocumentAction[] {
        if (this.voucher?.sourceType !== 'EXPENSE_PAYMENT') {
            const actions: AccountingDocumentAction[] = this.canVoid ? ['void'] : [];
            if (this.permissionService.hasPermission(app_permissions.accounts.voucher.cancel)) actions.push('cancel');
            return actions;
        }
        const actions: AccountingDocumentAction[] = [];
        if (this.permissionService.hasPermission(app_permissions.accounts.voucher.create)) actions.push('submit');
        if (this.permissionService.hasPermission(app_permissions.accounts.voucher.approve)) actions.push('approve', 'reject');
        if (this.permissionService.hasPermission(app_permissions.accounts.voucher.post)) actions.push('post');
        if (this.permissionService.hasPermission(app_permissions.accounts.voucher.cancel)) actions.push('cancel');
        if (this.permissionService.hasPermission(app_permissions.accounts.voucher.void)) actions.push('void');
        if (this.permissionService.hasPermission(app_permissions.accounts.voucher.reverse)) actions.push('reverse');
        return actions;
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private accountsService: AccountsService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        public crmPartyContext: CrmPartyContextService
    ) {}

    get canEditDraft(): boolean {
        return !!this.voucher?.voucherId && this.permissionService.hasPermission(app_permissions.accounts.voucher.edit)
            && isEditableFinancialDraft(this.voucher, this.sessionStorageService.getCompany()?.id);
    }

    editDraft(): void {
        if (this.canEditDraft) this.router.navigate(['/accounts/enterprise/payables/generated-vouchers/edit', this.voucher!.voucherId]);
    }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.canVoid = this.permissionService.hasPermission(app_permissions.accounts.voucher.void);
        this.load(id);
    }

    onTabChange(value: string | number) {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'allocations', 'bills', 'journal', 'attachments', 'activity', 'related'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    money(amount?: number, currencyCode = '') { return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`; }

    workflow(action: AccountingDocumentAction, reason?: string) {
        if (!this.voucher?.voucherId || !this.workflowActions.includes(action) || (action === 'cancel' && !isEditableFinancialDraft(this.voucher))) return;
        this.workflowLoadingAction = action;
        this.accountsService.runVoucherWorkflow(this.voucher.voucherId, action, {
            actorId: this.sessionStorageService.getCustomer()?.id,
            source: 'PORTAL',
            reason
        }).pipe(
            finalize(() => this.workflowLoadingAction = null)
        ).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Voucher updated', detail: `Voucher ${action} completed.` });
                this.load(this.voucher!.voucherId!);
            },
            error: err => this.messageService.add({
                severity: 'error',
                summary: 'Voucher action failed',
                detail: err?.error?.message || `The voucher could not be ${action}ed.`
            })
        });
    }

    private load(id: number) {
        this.pdfStatus = undefined;
        this.accountsService.getVoucherById(id).subscribe(voucher => this.voucher = voucher);
        this.accountsService.financePdfStatus('voucher', id).subscribe({ next: status => this.pdfStatus = status,
            error: () => this.pdfStatus = { status: 'MISSING', reprintAvailable: false } });
        this.accountsService.getAuditEvents('VOUCHER', id).subscribe(events => this.auditEvents = events);
        this.accountsService.getGlPreview('VOUCHER', id).subscribe(preview => this.glPreview = preview);
    }

    download() {
        if (!this.voucher?.voucherId || !this.documentActions.begin('download')) return;
        if (this.pdfStatus?.status === 'MISSING') {
            this.documentActions.end('download');
            this.messageService.add({ severity: 'warn', summary: 'Original unavailable', detail: 'No stored issued PDF is available for this voucher.' });
            return;
        }
        this.accountsService.downloadVoucherDocument(this.voucher.voucherId)
            .pipe(finalize(() => this.documentActions.end('download')))
            .subscribe({ next: pdf => this.accountsService.downloadPdfDocument(pdf, 'voucher', this.voucher?.voucherNumber, this.voucher?.voucherId, this.voucher?.postingStatus),
                error: () => this.messageService.add({ severity: 'error', summary: 'Document unavailable', detail: 'The voucher PDF is unavailable.' }) });
    }
    back() { this.router.navigate(['/accounts/enterprise/payables/generated-vouchers']); }
}
